import * as Sentry from "@sentry/vue";
import { getAppAccess, getAvailableApps } from "@/api";
import launchDarklyService from "@/services/launchdarkly";
import { patchAccessPermission } from "@/utils/accessPermissionPatch";
import { syncUserEmployeeInStorage } from "@components/utils/authEmployeeContext";
import { updateAccountCountsOnLogin } from "@/utils/accountCountUtils";
import { resetInMemoryFilterState } from "@/utils/resetInMemoryFilterState";

/**
 * Switch the logged-in user to `account` (ER-12027).
 *
 * Extracted from Login.vue onSelectAccount so the deep-link router guard can
 * reuse the exact same account-switch sequence before the Your Stuff access
 * check. `account` must be the full account object (id, name, currency, …) —
 * identifyTenant needs name/owner_email and referenceCurrency needs currency,
 * so an id alone is not enough.
 *
 * @param {{ id: number|string, name?: string, currency?: string, owner_email?: string }} account
 * @param {Record<string, unknown>} user
 * @param {{ emit?: (event: string, data: unknown) => void }} [options]
 * @returns {Promise<{ rawAccessPermission: object|null, patchedAccessPermission: object|null }>}
 */
export async function switchAccount(account, user, options = {}) {
  if (!account?.id) {
    throw new Error("no account");
  }
  if (!user?.id) {
    throw new Error("no user");
  }

  const { emit = null } = options;

  localStorage.setItem("currentAccountID", account.id);
  await launchDarklyService.identifyTenant(user, account);

  const response = await getAppAccess(account.id, user.id);
  const patchUserAccessEnabled = await launchDarklyService.getFlag("patch-user-access", false);
  const rawAccessPermission = response?.data;
  const patchedAccessPermission = patchAccessPermission({
    accessPermission: rawAccessPermission,
    accountId: account.id,
    user,
    isEnabled: patchUserAccessEnabled,
  });
  localStorage.setItem("accessPermission", JSON.stringify(patchedAccessPermission));
  // ER-10191: clear prior Access in-memory filters before entering the new Access.
  resetInMemoryFilterState();
  emit?.("accessPermissionUpdated", patchedAccessPermission);

  const accountDetailsResponse = await getAvailableApps(account.id);
  localStorage.setItem("currentAccountDetails", JSON.stringify(accountDetailsResponse?.data));
  emit?.("currentAccountDetailsUpdated", accountDetailsResponse?.data);

  syncUserEmployeeInStorage({
    user,
    accessPermission: patchedAccessPermission,
    accountId: account.id,
  });

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username || user.email,
    accountId: account.id,
    accountName: account.name,
  });

  await updateAccountCountsOnLogin(account.id);

  localStorage.setItem("referenceCurrency", account.currency);

  return { rawAccessPermission, patchedAccessPermission };
}
